import { useState, useRef, useEffect, useCallback } from 'react';
import '../styles/RecordingPlayer.css';
import { formatTime } from '../utils/formatTime';

interface TimestampMarker {
  time: number;
  comment: string;
}

interface RecordingPlayerProps {
  src: string;
  timestamps?: TimestampMarker[];
  onTimeUpdate?: (currentTime: number) => void;
}

export function RecordingPlayer({ src, timestamps, onTimeUpdate }: RecordingPlayerProps) {
  const mediaRef = useRef<HTMLVideoElement>(null);
  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [activeMarker, setActiveMarker] = useState<number | null>(null);

  useEffect(() => {
    const media = mediaRef.current;
    if (!media) return;

    function handleTimeUpdate() {
      const t = media!.currentTime;
      setCurrentTime(t);
      onTimeUpdate?.(t);
    }

    function handleLoadedMetadata() {
      setDuration(media!.duration);
    }

    function handleEnded() {
      setPlaying(false);
    }

    media.addEventListener('timeupdate', handleTimeUpdate);
    media.addEventListener('loadedmetadata', handleLoadedMetadata);
    media.addEventListener('ended', handleEnded);

    return () => {
      media.removeEventListener('timeupdate', handleTimeUpdate);
      media.removeEventListener('loadedmetadata', handleLoadedMetadata);
      media.removeEventListener('ended', handleEnded);
    };
  }, [onTimeUpdate]);

  const togglePlay = useCallback(() => {
    const media = mediaRef.current;
    if (!media) return;

    if (playing) {
      media.pause();
    } else {
      media.play().catch(console.error);
    }
    setPlaying(prev => !prev);
  }, [playing]);

  function handleProgressClick(e: React.MouseEvent<HTMLDivElement>) {
    const media = mediaRef.current;
    if (!media || !duration) return;

    const rect = e.currentTarget.getBoundingClientRect();
    const ratio = (e.clientX - rect.left) / rect.width;
    media.currentTime = ratio * duration;
  }

  function jumpToTimestamp(time: number, index: number) {
    const media = mediaRef.current;
    if (!media) return;

    media.currentTime = time;
    setActiveMarker(index);
    if (!playing) {
      media.play();
      setPlaying(true);
    }
  }

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  return (
    <div className="recording-player">
      {/* Video viewport */}
      <video
        ref={mediaRef}
        src={src}
        preload="metadata"
        playsInline
        className="recording-player-video"
      />

      <div className="recording-player-controls">
        <button
          className="recording-player-play"
          onClick={togglePlay}
          aria-label={playing ? 'Pause' : 'Play'}
        >
          {playing ? (
            <svg width="18" height="18" viewBox="0 0 18 18" fill="currentColor">
              <rect x="4" y="3" width="4" height="12" rx="1" />
              <rect x="10" y="3" width="4" height="12" rx="1" />
            </svg>
          ) : (
            <svg width="18" height="18" viewBox="0 0 18 18" fill="currentColor">
              <polygon points="4,2 16,9 4,16" />
            </svg>
          )}
        </button>

        <span className="recording-player-time">
          {formatTime(currentTime)}
        </span>

        <div className="recording-player-progress" onClick={handleProgressClick}>
          <div
            className="recording-player-progress-fill"
            style={{ width: `${progress}%` }}
          />
          {timestamps && duration > 0 && timestamps.map((ts, i) => {
            const markerPos = (ts.time / duration) * 100;
            if (markerPos > 100) return null;
            return (
              <button
                key={i}
                className={`recording-player-marker ${activeMarker === i ? 'active' : ''}`}
                style={{ left: `${markerPos}%` }}
                onClick={(e) => {
                  e.stopPropagation();
                  jumpToTimestamp(ts.time, i);
                }}
                title={`${formatTime(ts.time)} - ${ts.comment}`}
                aria-label={`Jump to ${formatTime(ts.time)}: ${ts.comment}`}
              />
            );
          })}
        </div>

        <span className="recording-player-time">
          {formatTime(duration)}
        </span>
      </div>

      {timestamps && timestamps.length > 0 && (
        <div className="recording-player-timestamps">
          {timestamps.map((ts, i) => (
            <button
              key={i}
              className={`recording-player-timestamp ${activeMarker === i ? 'active' : ''}`}
              onClick={() => jumpToTimestamp(ts.time, i)}
            >
              <span className="recording-player-timestamp-time">
                {formatTime(ts.time)}
              </span>
              <span className="recording-player-timestamp-comment">
                {ts.comment}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
